'use client';

import { API_ROUTES, HTTP_METHODS, useApiCacheControl, useApiInvalidate, useApiMutation } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { createDebug } from '@shared/debug';
import type { PlayerVerification } from '@shared/hooks';
import { ROUTES, useClientConfig } from '@shared/utils';
import { useEffect, useState } from 'react';
import { ErrorStep } from '../../components/ErrorStep';
import { useKycDialog } from '../../context/KycDialogContext';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { UploadStep } from './steps/UploadStep';
import type { ManualUploadStepProps, Step, UploadFilesResponse } from './types';

const debug = createDebug({ isEnabled: true });

function ManualUploadStep({ operationId }: ManualUploadStepProps) {
  const [currentStep, setCurrentStep] = useState<Step>('upload');
  const config = useClientConfig();
  const { setQueryData } = useApiCacheControl();
  const { invalidateQuery } = useApiInvalidate();

  const { hideCloseButton, hideBackButton, showBackButton, isRegistrationFlow, notifyKycCompleted } = useKycDialog();
  const { session } = useSession();
  const playerProfile = session?.profile ?? null;

  const { mutateAsync: uploadFiles, isPending: isUploading } = useApiMutation<UploadFilesResponse, FormData>(API_ROUTES.PLAYER_KYC.VERIFICATION.UPLOAD_FILES, HTTP_METHODS.POST);

  // Hide close button when this step is active
  useEffect(() => {
    hideCloseButton();
  }, [hideCloseButton]);

  // Hide back button only on confirmation step
  useEffect(() => {
    if (currentStep === 'confirmation') {
      hideBackButton();
    } else {
      showBackButton();
    }
  }, [currentStep, hideBackButton, showBackButton]);

  const handleSubmit = async (files: File[]) => {
    try {
      debug.log('[ManualUploadStep] Uploading files:', files);

      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      if (operationId) {
        formData.append('operationId', operationId);
      }

      const response = await uploadFiles(formData);

      debug.log('[ManualUploadStep] Upload successful:', response);

      setQueryData<PlayerVerification>(API_ROUTES.PLAYER_KYC.VERIFICATIONS, undefined, old =>
        old
          ? {
              ...old,
              verifications: old.verifications.map(verification => (verification.type === 'IDENTITY' ? { ...verification, status: 'PENDING' } : verification)),
            }
          : old
      );

      invalidateQuery(API_ROUTES.PLAYER_KYC.VERIFICATION.KYC_CARD);

      setCurrentStep('confirmation');
    } catch (error) {
      debug.forceError('[ManualUploadStep] Upload failed:', error);
      setCurrentStep('error');
    }
  };

  const handleRetry = () => {
    setCurrentStep('upload');
  };

  const handleDone = () => {
    const hadCallback = notifyKycCompleted();
    if (hadCallback || !isRegistrationFlow) return;
    window.location.href = `${config.legacySiteUrl}${ROUTES.REGISTER_REDIRECTION_SUCCESS}`;
  };

  const firstName = playerProfile?.firstName || 'User';

  if (currentStep === 'confirmation') {
    return <ConfirmationStep firstName={firstName} onDone={handleDone} />;
  }

  if (currentStep === 'error') {
    return <ErrorStep onRetry={handleRetry} isUploadFailure />;
  }

  return <UploadStep onSubmit={handleSubmit} isUploading={isUploading} />;
}

export { ManualUploadStep };
